import React, { useEffect, useMemo, useState } from 'react';
import * as api from '../api';
import Transcript from '../components/Transcript';
import RatingsPage from './RatingsPage';

export default function History() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(null);

  const [tab, setTab] = useState('unrated');

  const [page, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < 640 ? 5 : 8
  );

  const monthBgShort = ["Ян", "Фев", "Мар", "Апр", "Май", "Юни", "Юли", "Авг", "Сеп", "Окт", "Ное", "Дек"];
  const [filterType, setFilterType] = useState('all'); // 'all' | 'online' | 'campaign'

  function normalizeIsoUtc(s) {
    if (!s) return s;
    if (!/[zZ]$/.test(s) && !/[+-]\d{2}:\d{2}$/.test(s)) return s + "Z";
    return s;
  }

  function formatBgDate(isoStr) {
    if (!isoStr) return "";
    const d = new Date(normalizeIsoUtc(isoStr));
    if (isNaN(d)) return "";
    const day = d.getDate();
    const mon = monthBgShort[d.getMonth()];
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${day} ${mon} ${hh}:${mm}`;
  }

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const handler = (e) => setPageSize(e.matches ? 5 : 8);
    handler(mq);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        setItems(await api.history());
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const ratedCount = useMemo(() => items.filter(i => !!i.has_rating).length, [items]);
  const unratedCount = useMemo(() => items.filter(i => !i.has_rating).length, [items]);

  useEffect(() => setCurrentPage(1), [q, tab, filterType]);

  const filtered = useMemo(() => {
    const byName = items.filter(i => (i.filename || '').toLowerCase().includes(q.toLowerCase()));
    const byRated = tab === 'rated' ? byName.filter(i => !!i.has_rating) : byName.filter(i => !i.has_rating);

    if (filterType === 'online') {
      return byRated.filter(i => (i.call_type || 'Онлайн заявка') === 'Онлайн заявка');
    }
    if (filterType === 'campaign') {
      return byRated.filter(i => (i.call_type || 'Онлайн заявка') === 'Кампания');
    }
    return byRated;
  }, [items, q, tab, filterType]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  useEffect(() => {
    if (page > totalPages) setCurrentPage(totalPages);
  }, [totalPages, page]);

  const start = (page - 1) * pageSize;
  const pageItems = filtered.slice(start, start + pageSize);

  const viewItem = async (it) => {
    try {
      const t = await api.transcript(it.id);
      setSel({
        ...it,
        transcript: t.transcript,
        segments: Array.isArray(t.segments) ? t.segments : [],
        hasRating: !!it.has_rating
      });
    } catch (e) {
      console.error(e);
    }
  };

  const [exportOpen, setExportOpen] = useState(false);
  const [expType, setExpType] = useState('online'); // 'online' | 'campaign'
  const [tplOpts, setTplOpts] = useState([]);
  const [expTplId, setExpTplId] = useState('');

  useEffect(() => {
    if (!exportOpen) return;
    (async () => {
      try {
        const list = await api.listTemplates(expType);
        const opts = (Array.isArray(list) ? list : []).filter(t => (t.type || 'online') === expType);
        setTplOpts(opts);
        const def = opts.find(t => t.is_default);
        setExpTplId(def?.id || opts[0]?.id || '');
      } catch (e) {
        console.error(e);
        setTplOpts([]);
        setExpTplId('');
      }
    })();
  }, [exportOpen, expType]);

  const today = new Date();
  const y = today.getFullYear(), m = String(today.getMonth() + 1).padStart(2, "0"), d = String(today.getDate()).padStart(2, "0");
  const firstOfMonth = `${y}-${m}-01`;
  const todayStr = `${y}-${m}-${d}`;

  const [expFrom, setExpFrom] = useState(firstOfMonth);
  const [expTo, setExpTo] = useState(todayStr);
  const [expBusy, setExpBusy] = useState(false);


  return (
    <div className="card rounded-2xl p-6">
      {!sel ? (
        <>
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="text-lg font-bold whitespace-nowrap">Разговори</h2>

            <div className="flex items-center gap-2">
              <button
                className={`btn ${tab === 'unrated' ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setTab('unrated')}
              >
                Неоценени ({unratedCount})
              </button>
              <button
                className={`btn ${tab === 'rated' ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setTab('rated')}
              >
                Оценени ({ratedCount})
              </button>
            </div>



            <div className="ml-auto">
              <input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Търсене по име…"
                className="shrink-0 w-40 sm:w-72 md:w-80 bg-[#121825] border border-[#1f2638] rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-violet-600/40"
              />

              {tab === 'rated' && (
                <button className="btn btn-muted ml-2" onClick={() => setExportOpen(true)}>
                  Експорт
                </button>
              )}

            </div>
          </div>

          <div className="mt-3 sm:mt-4 pt-2 border-t border-[#1f2638]">
            <select
              className="w-44 sm:w-56 md:w-64 bg-[#121825] border border-[#1f2638] rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-violet-600/40"
              value={filterType}
              onChange={e => setFilterType(e.target.value)}
            >
              <option value="all">Всички видове</option>
              <option value="online">Онлайн заявка</option>
              <option value="campaign">Кампания</option>
            </select>
          </div>

          <div className="mt-4 overflow-x-auto max-w-full">
            <table className="w-full table">
              <thead>
                <tr className="text-left">
                  <th className="text-left">Файл</th>
                  <th className="text-right">Вид</th>
                  <th className="text-right">Дата</th>
                  {tab === 'rated' && <th className="text-right">Оценил</th>}
                  <th className="text-right">Статус</th>
                  <th className="text-right">Действия</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.length ? pageItems.map(h => (
                  <tr key={h.id}>
                    <td className="font-medium text-left">{h.filename}</td>

                    <td className="text-right">
                      <span
                        className={
                          "px-2 py-0.5 rounded-full text-[11px] " +
                          (h.call_type === "Кампания"
                            ? "bg-violet-900/30 border border-violet-700/40 text-violet-200"
                            : "bg-slate-700/30 border border-slate-600/40 text-slate-200")
                        }
                        title={h.call_type || "Онлайн заявка"}
                      >
                        {h.call_type || "Онлайн заявка"}
                      </span>
                    </td>

                    <td className="text-right text-slate-300">
                      {formatBgDate(h.created_at)}
                    </td>

                    {tab === 'rated' && (
                      <td className="text-right text-slate-300">
                        {h.evaluator_display && h.evaluator_display.trim()
                          ? h.evaluator_display
                          : "Не е посочен"}
                      </td>
                    )}

                    <td className="text-right">
                      {h.has_rating ? (
                        <span className="px-2 py-0.5 rounded-full bg-emerald-900/30 border border-emerald-700/40 text-emerald-200 text-[11px]">
                          Оценен
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full bg-slate-700/30 border border-slate-600/40 text-slate-200 text-[11px]">
                          Неоценен
                        </span>
                      )}
                    </td>

                    <td className="text-right">
                      <button
                        className="btn btn-ghost"
                        onClick={() => viewItem(h)}
                      >
                        Преглед
                      </button>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={tab === 'rated' ? 6 : 5} className="text-center py-10 text-slate-400">
                      Няма записи.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-start gap-2">
              <button className="btn btn-ghost" disabled={page <= 1} onClick={() => setCurrentPage(p => p - 1)}>Назад</button>
              <div className="text-sm text-slate-400">Стр. {page} / {totalPages}</div>
              <button className="btn btn-ghost" disabled={page >= totalPages} onClick={() => setCurrentPage(p => p + 1)}>Напред</button>
            </div>
          )}

          {exportOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center">
              <div className="absolute inset-0 bg-black/60" onClick={() => setExportOpen(false)} />
              <div className="relative z-10 w-[min(92vw,620px)] rounded-2xl border border-[#1f2638] bg-[#0e1320] p-5">
                <div className="text-lg font-semibold mb-4">Експорт на оценени разговори</div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-slate-300">От дата</label>
                    <input
                      type="date"
                      className="w-full bg-[#121825] border border-[#1f2638] rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-violet-600/40"
                      value={expFrom}
                      onChange={e => setExpFrom(e.target.value)}
                      max={expTo}
                    />
                  </div>
                  <div>
                    <label className="text-sm text-slate-300">До дата</label>
                    <input
                      type="date"
                      className="w-full bg-[#121825] border border-[#1f2638] rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-violet-600/40"
                      value={expTo}
                      onChange={e => setExpTo(e.target.value)}
                      min={expFrom}
                    />
                  </div>

                  <div>
                    <label className="text-sm text-slate-300">Тип разговор</label>
                    <select
                      className="w-full bg-[#121825] border border-[#1f2638] rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-violet-600/40"
                      value={expType}
                      onChange={e => setExpType(e.target.value)}
                    >
                      <option value="online">Онлайн заявка</option>
                      <option value="campaign">Кампания</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-sm text-slate-300">Темплейт</label>
                    <select
                      className="w-full bg-[#121825] border border-[#1f2638] rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-violet-600/40"
                      value={expTplId}
                      onChange={e => setExpTplId(e.target.value)}
                      disabled={!tplOpts.length}
                    >
                      {tplOpts.map(t => (
                        <option key={t.id} value={t.id}>
                          {t.name}{t.is_default ? ' (default)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mt-5 flex items-center justify-end gap-2">
                  <button className="btn btn-ghost" onClick={() => setExportOpen(false)}>Отказ</button>
                  <button
                    className="btn btn-primary"
                    disabled={expBusy || !expFrom || !expTo || !expTplId}
                    onClick={async () => {
                      try {
                        setExpBusy(true);
                        await api.exportRatingsRange(expFrom, expTo, expType, expTplId);
                        setExportOpen(false);
                      } catch (e) {
                        console.error(e);
                        alert(e?.message || "Грешка при експорт.");
                      } finally {
                        setExpBusy(false);
                      }
                    }}
                  >
                    {expBusy ? "Експорт…" : "Експорт"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold">
              Транскрипт — {sel.filename}
            </h2>
            <div className="flex items-center gap-2">
              {sel.hasRating && (
                <button
                  className="btn btn-muted"
                  onClick={() => api.exportRatingExcel(sel.id)}
                >
                  Експорт Excel
                </button>
              )}
              <button
                className="btn btn-muted"
                onClick={() => api.downloadFile(sel.id)}
              >
                Свали
              </button>
            </div>
          </div>

          <div className="flex flex-col lg:flex-row gap-6">
            <div className="lg:w-1/2 space-y-4">
              <div className="border border-[#1f2638] rounded-xl p-4 bg-[#0e1320]/40">
                <div className="max-h-[70vh] overflow-y-auto nice-scrollbar">
                  <Transcript text={sel.transcript} segments={sel.segments} />
                </div>
              </div>

            </div>

            <div className="lg:w-1/2">
              <RatingsPage
                fileId={sel.id}
                onSaved={() => setSel(prev => prev ? { ...prev, hasRating: true } : prev)}
              />
            </div>
          </div>

          <div className="mt-6">
            <button className="btn btn-ghost" onClick={() => setSel(null)}>
              ← Назад към разговори
            </button>
          </div>
        </>
      )}
    </div>
  );
}
